import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/ui/Modal.tsx';
import { BreadcrumbItem, Breadcrumbs } from '@components/navigation/Breadcrumb.tsx';
import ProductImage from './comp/ProductImage';
import ProductInfo from './comp/ProductInfo';
import SupplierAvailabilityTag from '@features/supplier-availability/SupplierAvailabilityTag.tsx';
import ProductPricing from './comp/ProductPricing';
import ProductTabs from './comp/ProductTabs';

import { useProductDetail } from '@features/product-detail/useProductDetail.ts';
import Tag from '@components/ui/Tag.tsx';
import CustomProductBadge from '@features/client/custom-products/CustomProductBadge';
import AlternateProductsTab from '@features/product-detail/comp/AlternateProductsTab.tsx';
import { ClientProductDetailResponse } from '@api/generated/search-service';
import { useProductModal } from '@hooks/useProductModal.ts';
import SuppliersList, { Supplier } from '@features/product-detail/comp/SuppliersList.tsx';
import { useCart } from '@hooks/useCart.ts';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { sortDetailSuppliersBestFirst } from '@utils/priceVariants.ts';
import { useFavouritesStore } from '@features/favouries/useFavouritesStore.ts';
import { ProductDetailTab } from '@features/product-detail/types.ts';
import { useRouter } from '@tanstack/react-router';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import AddSupplierSidebar from '@features/product-detail/comp/AddSupplierSidebar.tsx';
import { useHasPermission } from '@hooks/useHasPermission.ts';
import { PermissionTypesEnum } from '@mock/Permissions.ts';
import NoOfferEmptyState from '@features/product-detail/comp/NoOfferEmptyState.tsx';
import { useCreatePreferredDelivery } from '@features/client/preferred-delivery/useCreatePreferredDelivery.ts';
import { refreshProductTile } from '@features/product-detail/productTileUpdates.ts';

interface ComponentProps {
  visible: boolean;
  productId: number;
  selectedTab: ProductDetailTab;
}

const ProductDetailModal: React.FC<ComponentProps> = ({ visible, productId, selectedTab = 'detail' }) => {
  const { t } = useTranslation('client');
  const { openModal, closeModal } = useProductModal();
  const { productDetail, isLoading, error, loadProductDetail, clearProductDetail, selectedVariant, setSelectedVariant, setSupplierPreference, isExpiredPrice } =
    useProductDetail();
  const { setItemCount, deleteCartItem } = useCart();
  const cartItems = useCart(s => s.items);
  const { loadFavourites, favouritesIds, removeFromFavourites, addToFavourites, storingId } = useFavouritesStore();
  const router = useRouter();
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const canAddManualSupplier = useHasPermission(useOrgPortal.getState().orgId, useOrgPortal.getState().portalId, PermissionTypesEnum.CLIENT_MANUAL_SUPPLIER_ADD);
  const preferredDelivery = useCreatePreferredDelivery(s => s.data);

  const onHide = () => {
    closeModal();
  };

  const productCartItems = useMemo(() => {
    return cartItems.filter(t => t.productId === productId);
  }, cartItems);

  // Načtení dat produktu při změně productId
  useEffect(() => {
    if (visible && productId && loadProductDetail) {
      loadFavourites();
      loadProductDetail(productId, selectedTab);
    } else if (!visible) {
      clearProductDetail();
    }
  }, [visible, productId, loadProductDetail, clearProductDetail]);

  const toggleFavorite = useCallback((productId: number, isCurrentlyFavorite: boolean, productName?: string) => {
    if (isCurrentlyFavorite) {
      removeFromFavourites(productId, productName);
    } else {
      addToFavourites(productId, productName);
    }
  }, []);

  const isFavorite = useMemo(() => {
    const favouritesSet = new Set(favouritesIds);
    return favouritesSet.has(productId);
  }, [favouritesIds]);

  const selectedSupplier = selectedVariant ? productDetail?.productSuppliers.find(t => t.priceVariants.some(tt => tt.id === selectedVariant.id)) : undefined;
  const selectedCartItem = selectedVariant ? productCartItems.find(t => t.priceVariantId === selectedVariant.id) : undefined;

  const createBreadcrumb = () => {
    return (
      <Breadcrumbs
        items={
          productDetail?.categoryBreadcrumbInfo.map(t => ({
            label: t.categoryName,
            href: router.buildLocation({
              to: '/c/$orgId/$portalId/catalog/$categoryEcid',
              params: {
                orgId: useOrgPortal.getState().orgId,
                portalId: useOrgPortal.getState().portalId,
                categoryEcid: t.categoryEcid,
              },
            }).href,
          })) as BreadcrumbItem[]
        }
      />
    );
  };

  // Error stav
  if (error) {
    return (
      <Modal
        visible={visible}
        onHide={onHide}
        closeOnBackdrop
        header={<div className="self-stretch justify-start text-black text-2xl font-semibold font-brand leading-loose">{t('productDetail.error')}</div>}
      >
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-600">{t('productDetail.loadError', { error })}</div>
        </div>
      </Modal>
    );
  }

  // Loading stav — během fetche neukazovat "nenalezeno"
  if (isLoading || !productDetail) {
    const isReallyMissing = !isLoading && !productDetail;
    return (
      <Modal
        visible={visible}
        onHide={onHide}
        closeOnBackdrop
        header={
          <div className="self-stretch justify-start text-black text-2xl font-semibold font-brand leading-loose">
            {isReallyMissing ? t('productDetail.notFoundTitle') : t('productDetail.loadingTitle')}
          </div>
        }
      >
        <div className="flex justify-center items-center h-64 gap-3">
          {isReallyMissing ? (
            <div className="text-lg text-gray-600">{t('productDetail.notFound')}</div>
          ) : (
            <>
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
              <div className="text-lg text-gray-600">{t('productDetail.loading')}</div>
            </>
          )}
        </div>
      </Modal>
    );
  }

  const tabsData = [
    {
      id: 'detail' as ProductDetailTab,
      label: t('productDetail.detailTab'),
    },
    productDetail?.alternateProducts?.length > 0
      ? {
          id: 'alternatives' as ProductDetailTab,
          label: t('productDetail.alternativesTab'),
          badge: productDetail.isCheaperAlternative ? (
            <Tag text={t('productDetail.buyForLess')} color={'success'} colorSchema={'dark'} componentState={'default'} size={'xs'} />
          ) : (
            <></>
          ),
        }
      : null,
  ].filter(t => isNotNullOrUndefined(t));

  const isCustom = (productDetail as any)?.isCustomProduct === true;
  const isManualSupplier = selectedVariant?.isManual === true;

  // Empty state: pro vybrané datum dodání ani jeden dodavatel nemá platnou cenovou variantu.
  // BE už filtruje per-supplier (ClientProductReadService) → priceVariants jsou prázdné.
  const noOffer = !selectedVariant || (productDetail?.productSuppliers ?? []).every(s => (s.priceVariants?.length ?? 0) === 0);
  const hasAlternatives = (productDetail?.alternateProducts?.length ?? 0) > 0;

  const getProductDetail = (productDetail: ClientProductDetailResponse) => {
    return (
      <>
        {/* Hlavní obsah - responzivní layout */}
        <div className="w-full flex flex-col lg:flex-row gap-6">
          {/* Obrázek produktu */}
          <div className="w-full lg:flex-1">
            <ProductImage
              favUpdating={storingId == productId}
              imageUrls={productDetail?.imagesUrls}
              isFavorite={isFavorite}
              onFavoriteToggle={() => toggleFavorite(productId, isFavorite)}
              tags={[]}
              grayscale={isExpiredPrice}
              badge={
                <>
                  {isCustom && <CustomProductBadge />}
                  {isManualSupplier && <Tag text={t('productDetail.addSupplier')} color="primary" colorSchema="light" componentState="default" size="xs" />}
                  {productDetail.hasSupplierPreference && (
                    <Tag text={t('productDetail.supplierPreferenceTag')} color="secondary" colorSchema="dark" componentState="default" size="xs" />
                  )}
                  <SupplierAvailabilityTag availability={selectedSupplier?.availability} />
                </>
              }
            />
          </div>

          {/* Ceny a objednávka — nebo empty-state, když dodavatelé nemají platnou cenu pro vybrané datum */}
          <div className="w-full lg:flex-1">
            {noOffer ? (
              <NoOfferEmptyState
                deliveryDate={preferredDelivery?.deliveryDate}
                hasAlternatives={hasAlternatives}
                onShowAlternatives={() => openModal(productId!!, 'alternatives')}
              />
            ) : (
              <ProductPricing
                productId={productDetail.productId}
                onAddToCart={q => {
                  setItemCount({
                    productId: productId,
                    priceVariantId: selectedVariant!!.id,
                    quantity: q,
                    supplierId: selectedSupplier?.supplierId!!,
                    productName: productDetail.productName,
                  });
                }}
                onDeleteCartItem={() => deleteCartItem(selectedCartItem!!.id, productDetail.productName)}
                priceVariant={selectedVariant!!}
                quantityInCart={selectedCartItem?.quantity}
                minAmount={selectedSupplier?.catalogData?.minimumQty ?? 1}
                onlyMultiply={selectedSupplier?.catalogData?.multiply ?? false}
                vat={selectedVariant?.vatPercentage || 0}
                isExpired={isExpiredPrice}
              />
            )}
          </div>

          {/* Informace o produktu — v empty-state režimu jen intrinsická data (ECID, min/multiples) */}
          <div className="w-full lg:flex-1">
            <ProductInfo
              compact={noOffer}
              details={{
                ecid: productDetail.productEcid,
                supplier: selectedSupplier?.supplierName ?? 'N/A',
                catalogNumber: selectedSupplier?.catalogData?.catalogCode ?? 'N/A',
                catalogName: selectedSupplier?.catalogData?.catalogName ?? 'N/A',
                minAmount: selectedSupplier?.catalogData?.minimumQty ?? 1,
                onlyMultiple: selectedSupplier?.catalogData?.multiply ?? true,
                description: '',
                deliveryDays: selectedSupplier?.deliveryDays ?? [],
                // Aukce se vaze na konkretni cenu, ne na dodavatele — bere se z vybrane varianty.
                auction: selectedVariant?.auctionName ? [selectedVariant.auctionName, selectedVariant.auctionCode].filter(Boolean).join(' · ') : null,
                auctionId: selectedVariant?.auctionId,
                supplierNote: selectedSupplier?.catalogData?.supplierNote,
                allergens: productDetail.allergens,
              }}
            />
          </div>
        </div>
        {/* Seznam dodavatelů */}
        {selectedVariant && (
          <SuppliersList
            productId={productDetail.productId}
            selectedPriceVariant={selectedVariant}
            suppliers={sortDetailSuppliersBestFirst(
              productDetail.productSuppliers.reduce((acc, supplierData) => {
                supplierData.priceVariants.forEach(v => {
                  acc.push({
                    supplierName: supplierData.supplierName,
                    priceVariant: v,
                    supplierId: supplierData.supplierId,
                    onlyMultiply: supplierData.catalogData?.multiply ?? false,
                    minimumQuantity: supplierData.catalogData?.minimumQty ?? 1,
                    auctionAmount: productDetail.auctionAmount,
                    auctionUnit: productDetail.auctionUnit,
                    supplierPreference: supplierData.supplierPreference,
                    deliveryDate: supplierData.deliveryDate,
                    availability: supplierData.availability,
                  });
                });
                return acc;
              }, [] as Supplier[]),
            )}
            onSetSupplierPreference={(supplierId, preference) => setSupplierPreference(productId, supplierId, preference)}
            onSelectPriceVariant={setSelectedVariant}
            onAddToCart={(supplierId, v, q) =>
              setItemCount({
                productId: productId,
                priceVariantId: v.id,
                quantity: q,
                supplierId: supplierId,
                productName: productDetail.productName,
              })
            }
            onDeleteCartItem={id => deleteCartItem(id)}
            onAddSupplier={canAddManualSupplier ? () => setAddSupplierOpen(true) : undefined}
          />
        )}

        <AddSupplierSidebar
          isOpen={addSupplierOpen}
          onClose={() => setAddSupplierOpen(false)}
          productId={productDetail.productId}
          productName={productDetail.productName}
          productImageUrl={productDetail.imagesUrls?.[0]}
          organizationId={useOrgPortal.getState().orgId}
          portalId={useOrgPortal.getState().portalId}
          auctionAmount={productDetail.auctionAmount}
          auctionUnit={productDetail.auctionUnit}
          onSuccess={() => {
            loadProductDetail(productId, selectedTab);
            // Nový dodavatel mění i ceny na dlaždici a z detailu se spočítat nedá — dotáhne se celá.
            refreshProductTile(productId);
          }}
        />
      </>
    );
  };

  const getAlternatives = (productDetail: ClientProductDetailResponse) => {
    return (
      <AlternateProductsTab
        originalProduct={{
          name: productDetail.productName,
          supplierName: '',
          ecid: productDetail.productEcid,
          imageUrl: productDetail.imagesUrls[0],
          priceVariant: selectedVariant,
          auctionAmount: productDetail.auctionAmount,
          auctionUnit: productDetail.auctionUnit,
        }}
        alternateProducts={productDetail.alternateProducts.map(alt => ({
          name: alt.productName,
          supplierName: alt.supplierName,
          ecid: alt.productEcid,
          auctionPrice: alt.auctionPrice,
          productId: alt.productId,
          imageUrl: alt.imageUrl,
          isCheaperAlternative: alt.isCheaperAlternative,
          isCheapestAlternative: alt.isCheapestAlternative,
        }))}
      />
    );
  };

  return (
    <>
      <Modal
        visible={visible}
        onHide={onHide}
        closeOnBackdrop
        header={<div className="self-stretch justify-start text-black text-2xl font-semibold font-brand leading-loose">{productDetail.productName}</div>}
      >
        {createBreadcrumb()}
        {/* Tabs */}
        <ProductTabs onTabChange={v => openModal(productId!!, v as ProductDetailTab)} tabs={tabsData} activeTabId={selectedTab} />

        {selectedTab === 'detail' && getProductDetail(productDetail)}
        {selectedTab === 'alternatives' && getAlternatives(productDetail)}
      </Modal>
    </>
  );
};

export default ProductDetailModal;
